import { NotFoundError } from '@cian/peperrors/shared';

import {
  MANIFEST_API_RUNTIME_NAME,
  type IImageManifest,
  type IRegistryMicrofrontendManifest,
} from '../../../shared/manifest/index.js';
import { Image } from '../image/index.js';

import type { MicrofrontendNS } from '../../../shared/microfrontend/index.js';
import type { PartialBy } from '../../../shared/types/typescript/index.js';
import type { IImage } from '../../image/index.js';
import type { IMicrofrontend } from '../../microfrontend/index.js';
import type { IAPIRuntime } from '../../runtime/index.js';
import type { AssetsStorage } from '../assets/AssetsStorage.js';
import type { IImageContext } from '../context/index.js';

export class Microfrontend implements IMicrofrontend {
  public readonly name: string;

  private readonly _images: Map<MicrofrontendNS.ImageVersion, Image>;
  private readonly assetsStorage: AssetsStorage;

  public constructor(options: IRegistryMicrofrontendManifest, assetsStorage: AssetsStorage) {
    const { images = {}, name } = options;

    this.name = name;
    this.assetsStorage = assetsStorage;

    this._images = new Map(
      Object.entries(images).map(([version, image]) => [version, new Image(image, assetsStorage, this.imageContext)]),
    );
  }

  public get images() {
    return Object.fromEntries<IImage>(this._images);
  }

  private get imageContext(): IImageContext {
    return { microfrontend: this };
  }

  public api(): IAPIRuntime | undefined {
    for (const image of Object.values(this.images)) {
      const runtime = image.runtime<IAPIRuntime>(MANIFEST_API_RUNTIME_NAME);

      if (runtime) {
        return runtime;
      }
    }

    return undefined;
  }

  public image(version: MicrofrontendNS.ImageVersion): IImage | undefined {
    return this._images.get(version);
  }

  public imageStrict(version: MicrofrontendNS.ImageVersion): IImage {
    const image = this.image(version);
    if (!image) {
      throw new NotFoundError({
        message: `Образ версии "${version}" не найден в реестре (${this.name})`,
        domain: 'mf-registry.browser.microfrontend',
      });
    }

    return image;
  }

  public newImage(manifest: PartialBy<IImageManifest, 'assets' | 'bundles' | 'runtimes'>): IImage {
    const image = new Image(
      { assets: {}, bundles: {}, runtimes: {}, ...manifest },
      this.assetsStorage,
      this.imageContext,
    );

    this._images.set(image.version, image);

    return image;
  }
}
